import { formatMin } from '@wiztime/core';
import { utcDateFormatter } from './dateFormatter';

/** "06:00–08:00": wall-minute span formatted as clock times (1440 reads 00:00). */
export function formatRange(startMin: number, endMin: number): string {
  return `${formatMin(startMin)}–${formatMin(endMin)}`;
}

/** "2h", "1h30", "45m": a duration in minutes. */
export function formatDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

/** "0h", "2h", "2.5h": minutes as hours with at most one decimal (stats line). */
export function formatHours(minutes: number): string {
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

/**
 * Label of a personal date: "10月7日 周三" (zh-CN) or "Wed, Oct 7" (en); the year is added
 * when it differs from `referenceYear`.
 */
export function formatDateLabel(date: string, locale: string, referenceYear: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const utc = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  const withYear = year !== referenceYear;
  const format = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
    utcDateFormatter(locale, options);
  if (locale.startsWith('zh')) {
    const weekday = format({ weekday: 'short' }).format(utc);
    return `${withYear ? `${year}年` : ''}${month}月${day}日 ${weekday}`;
  }
  return format({
    year: withYear ? 'numeric' : undefined,
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  }).format(utc);
}

/** Label of a month (`YYYY-MM`): "2026 年 10 月" (zh-CN) or "October 2026" (en). */
export function formatMonthLabel(month: string, locale: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (locale.startsWith('zh')) return `${year} 年 ${monthNumber} 月`;
  const utc = new Date(Date.UTC(year ?? 1970, (monthNumber ?? 1) - 1, 1));
  return utcDateFormatter(locale, { year: 'numeric', month: 'long' }).format(utc);
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** "2026-10-12 14:30": an absolute instant in the device's time zone (a revision time). */
export function formatInstant(ms: number): string {
  const local = new Date(ms);
  return `${local.getFullYear()}-${pad2(local.getMonth() + 1)}-${pad2(local.getDate())} ${pad2(local.getHours())}:${pad2(local.getMinutes())}`;
}

/**
 * The "last updated" cell of the settings lists: "today 14:30" (via `todayLabel`) on the same
 * calendar day as `nowMs`, "10-11 14:30" in the same year, "2025-10-11" before; all in the
 * device's time zone.
 */
export function formatLastUpdated(ms: number, nowMs: number, todayLabel: (time: string) => string): string {
  const local = new Date(ms);
  const now = new Date(nowMs);
  const time = `${pad2(local.getHours())}:${pad2(local.getMinutes())}`;
  const monthDay = `${pad2(local.getMonth() + 1)}-${pad2(local.getDate())}`;
  if (local.getFullYear() !== now.getFullYear()) return `${local.getFullYear()}-${monthDay}`;
  if (local.getMonth() === now.getMonth() && local.getDate() === now.getDate()) return todayLabel(time);
  return `${monthDay} ${time}`;
}
